import React from 'react';

interface Season {
  name: string;
  months: string;
  waterTemp: string;
  visibility: string;
  marineLife: string[];
  conditions: string;
  icon: string;
  color: string;
}

interface SeasonCardProps {
  season: Season;
}

const SEASON_ICONS: Record<string, React.ReactNode> = {
  spring: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
      <path d="M12 22V12M12 12C12 7 8 3 3 3c0 5 4 9 9 9zM12 12c0-5 4-9 9-9-1 5-5 9-9 9z"/>
    </svg>
  ),
  summer: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
    </svg>
  ),
  autumn: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
      <path d="M17 8C8 10 5.9 16.17 3.82 19.5c1.5 1 3.18 1.5 5.18 1.5 4 0 8-2 10-6 2-4 1-9-2-7z"/>
    </svg>
  ),
  winter: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
      <line x1="12" y1="2" x2="12" y2="22"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
};

const SeasonCard = ({ season }: SeasonCardProps) => {
  const getBorderColor = (colorClass: string) => {
    if (colorClass.includes('secondary')) return '#6FD3E0';
    if (colorClass.includes('warning')) return '#FF8A3D';
    if (colorClass.includes('accent')) return '#FF8A3D';
    if (colorClass.includes('primary')) return '#6FD3E0';
    return '#6FD3E0';
  };

  const borderColor = getBorderColor(season.color);
  const iconNode = SEASON_ICONS[season.icon] ?? SEASON_ICONS['summer'];

  return (
    <div
      style={{ backgroundColor: '#0F2234', borderLeft: `4px solid ${borderColor}`, border: `1px solid rgba(111,211,224,0.12)`, borderLeftWidth: '4px', borderLeftColor: borderColor }}
      className="rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
    >
      <div className="flex items-center space-x-3 mb-4">
        <div
          style={{ backgroundColor: `${borderColor}18`, color: borderColor }}
          className="w-12 h-12 rounded-full flex items-center justify-center"
        >
          {iconNode}
        </div>
        <div>
          <h3 style={{ color: '#EAF4F8' }} className="text-xl font-headline font-bold">{season.name}</h3>
          <p style={{ color: '#C8DDE6' }} className="text-sm">{season.months}</p>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-start space-x-2">
          <span style={{ color: '#6FD3E0' }} className="font-medium text-sm min-w-[100px]">Water Temp:</span>
          <span style={{ color: '#EAF4F8' }} className="text-sm">{season.waterTemp}</span>
        </div>
        <div className="flex items-start space-x-2">
          <span style={{ color: '#6FD3E0' }} className="font-medium text-sm min-w-[100px]">Visibility:</span>
          <span style={{ color: '#EAF4F8' }} className="text-sm">{season.visibility}</span>
        </div>
        <div className="flex items-start space-x-2">
          <span style={{ color: '#6FD3E0' }} className="font-medium text-sm min-w-[100px]">Conditions:</span>
          <span style={{ color: '#EAF4F8' }} className="text-sm">{season.conditions}</span>
        </div>
        <div style={{ borderTop: '1px solid rgba(111,211,224,0.12)' }} className="pt-3">
          <p style={{ color: '#6FD3E0' }} className="font-medium text-sm mb-2">Marine Life Activity:</p>
          <div className="flex flex-wrap gap-2">
            {season.marineLife.map((life, index) => (
              <span key={index} style={{ backgroundColor: 'rgba(111,211,224,0.1)', color: '#6FD3E0', border: '1px solid rgba(111,211,224,0.2)' }} className="px-3 py-1 text-xs rounded-full">
                {life}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonCard;